import { AfterViewInit, Component, ElementRef, HostListener, NgZone, OnInit, ViewChild } from '@angular/core';
import { MenuItem } from 'primeng/api';
import { MultiSelect } from 'primeng/multiselect';
import { BaseComponent } from 'src/app/core/components/base.component';
import { AppContextService } from 'src/app/core/services/app-context.service';
import { NavigationService } from 'src/app/core/services/navigation.service';
import { SecurityService } from 'src/app/core/services/security.service';
import { ThemeModeService } from 'src/app/core/services/theme-mode.service';
import { Globals } from '../../globals';
import { MainComponent } from '../main/main.component';

@Component({
	selector: 'app-header',
	templateUrl: './header.component.html',
	standalone: false,
})
export class HeaderComponent extends BaseComponent implements OnInit, AfterViewInit {
	filterSelected: string;
	profileMenuItems: MenuItem[] = [];
	@ViewChild('viewByMultiselect') ms: MultiSelect;
	@ViewChild('profileMenuPanel') profileMenuPanel?: ElementRef<HTMLDivElement>;
	@ViewChild('profileMenuTrigger') profileMenuTrigger?: ElementRef<HTMLElement>;
	profileMenuOpen = false;
	isDark = false;

	constructor(
		public appMain: MainComponent,
		appContext: AppContextService,
		public globals: Globals,
		private navigationService: NavigationService,
		private securityService: SecurityService,
		private themeModeService: ThemeModeService,
		private ngZone: NgZone
	) {
		super(appContext);
		this.themeModeService.themeMode$.subscribe(mode => {
			console.log('Theme mode changed to:', mode);
			this.isDark = this.themeModeService.isDarkMode();
		});
	}

	ngOnInit() {
		this.initializeProfileMenu();
	}

	ngAfterViewInit() {
		if (this.ms != null) {
			const originalRemoveOption = this.ms.removeOption;
			this.ms.removeOption = (...args) => {
				originalRemoveOption.apply(this.ms, args);
				this.onViewByFilterPanelHide();
			};
		}
	}

	toggleTheme() {
		this.themeModeService.toggleTheme();
		this.initializeProfileMenu();
	}

	getThemeIcon(): string {
		return this.isDark ? 'pi pi-sun' : 'pi pi-moon';
	}

	initializeProfileMenu() {
		this.profileMenuItems = [
			{
				label: 'Profile',
				icon: 'pi pi-user-edit',
				visible: this.showProfileMenu(),
				command: () => {
					this.viewUserProfile();
					this.hideProfileMenu();
				},
			},
			{
				label: 'Change Subscription',
				icon: 'pi pi-sitemap',
				visible: this.showChangeSubscription(),
				command: () => {
					this.viewSubscriptions();
					this.hideProfileMenu();
				},
			},
			{
				label: 'Toggle Theme',
				icon: this.getThemeIcon(),
				command: () => {
					this.toggleTheme();
					this.hideProfileMenu();
				},
			},
			{
				separator: true,
			},
			{
				label: 'Sign out',
				icon: 'pi pi-power-off',
				command: () => {
					this.logout();
					this.hideProfileMenu();
				},
			},
		];
	}

	onProfileMenuToggle(event: MouseEvent) {
		event.stopPropagation();
		this.profileMenuOpen = !this.profileMenuOpen;
	}

	hideProfileMenu() {
		this.profileMenuOpen = false;
	}

	@HostListener('document:click', ['$event'])
	onDocumentClick(event: Event) {
		if (!this.profileMenuOpen) {
			return;
		}

		const target = event.target as HTMLElement;
		const withinTrigger = this.profileMenuTrigger?.nativeElement?.contains(target);
		const withinPanel = this.profileMenuPanel?.nativeElement?.contains(target);

		if (!withinTrigger && !withinPanel) {
			this.hideProfileMenu();
		}
	}

	getDisplayName(): string {
		return this.appContext.user.DisplayName;
	}

	getSubscriberName(): string {
		return this.appContext.user.SubscriberName;
	}

	showProfileMenu(): boolean {
		return this.appContext.userHasValidSubscription();
	}

	logout() {
		this.securityService.logout();
	}

	showDemoBadge(): boolean {
		return this.appContext?.user?.IsDemoSubscription;
	}

	showTestBadge(): boolean {
		return this.appContext?.user?.IsTestSubscription;
	}

	showEnvironmentBadge(): boolean {
		return this.appContext?.user?.EnvironmentTag != null && this.appContext?.user?.EnvironmentTag != '';
	}

	getEnvironmentBadge(): string {
		return 'environment: ' + this.appContext?.user?.EnvironmentTag;
	}

	viewUserProfile() {
		this.appContext.isExternalUser() ? this.navigationService.navigateToCurrentExternalUserProfile() : this.navigationService.navigateToCurrentUserProfile();
	}

	viewSubscriptions() {
		this.navigationService.navigateToCurrentSubscriptions();
	}

	onViewByFilterPanelHide() {
		this.ngZone.run(() => {
			this.appContext.onClientCompanyChanged();
		});
	}

	onViewByFilterChange(ev) {
		this.filterSelected = '';
		ev.value.forEach(dropdownSelected => {
			this.filterSelected += dropdownSelected + ';';
		});
		const previousSelection = this.appContext.getViewByFilter();
		this.appContext.previousClientCompanySelection = previousSelection;

		this.appContext.setSavedValue('ViewBy-Dropdown', this.filterSelected);
	}

	showChangeSubscription(): boolean {
		return this.appContext.user.HasMultipleSubscriptions === true;
	}
}
